// components/CommissionSettings.tsx
import React from "react";
import { Form, Formik, useFormikContext } from "formik";
import styled from "@emotion/styled";
import { DollarSign, AlertCircle } from "lucide-react";
import theme from "@utils/theme";
import Button from "@components/button";
import Fields from "@components/fields";
import user from "@apis/user";
import { Helper } from "@utils/index";

const colors = {
  primary: theme.orange.orange500,
  primaryLight: theme.orange.orange100,
  primaryDark: theme.orange.orange600,
  error: "#ef4444",
  gray50: "#f8fafc",
  gray100: "#f1f5f9",
  gray200: "#e2e8f0",
  gray300: "#cbd5e1",
  gray400: "#94a3b8",
  gray500: "#64748b",
  gray600: "#475569",
  gray700: "#334155",
  gray800: "#1e293b",
  white: "#ffffff",
};

const CommissionPlan = styled.div`
  background: ${colors.gray50};
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${colors.gray200};
  margin-bottom: 16px;
`;

const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const PlanIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
`;

const PlanTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.gray800};
  margin: 0;
`;

const PlanDescription = styled.p`
  font-size: 13px;
  color: ${colors.gray600};
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

const CommissionInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CommissionInput = styled.input<any>`
  padding: 8px 12px;
  border: 1px solid ${colors.gray300};
  border-radius: 6px;
  font-size: 13px;
  background: ${colors.white};
  width: 100px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px ${colors.primaryLight};
  }
`;

const CommissionLabel = styled.span`
  font-size: 13px;
  color: ${colors.gray600};
  font-weight: 500;
`;

const ErrorMessage = styled.div<any>`
  color: ${colors.error};
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface CommissionSettingsProps {
  isActive?: boolean;
  onSwitchPlan?: () => void;
}

const CommissionSettings: React.FC<CommissionSettingsProps> = ({
  isActive = true,
  onSwitchPlan,
}) => {
  const user = Helper.getUser();
  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    useFormikContext() as any;

  return (
    <CommissionPlan>
      <PlanHeader>
        <PlanIcon>
          <DollarSign size={16} />
        </PlanIcon>
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <PlanTitle>Commission Base Plan</PlanTitle>
              <PlanDescription>
                {values.commissionRate === "0"
                  ? "0% Commission per order. Restaurant will pay 0% Commission to Zedfo From each order. You will get access of all the features and options in restaurant panel, app and interaction with user."
                  : `${values.commissionRate}% Commission per order. Restaurant will pay ${values.commissionRate}% Commission to Zedfo From each order.`}
              </PlanDescription>
            </div>
            {onSwitchPlan && (
              <button
                type="button"
                onClick={onSwitchPlan}
                style={{
                  padding: "6px 12px",
                  background: colors.primary,
                  color: colors.white,
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: "500",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Change Plan
              </button>
            )}
          </div>
        </div>
      </PlanHeader>

      <Fields.Switch name="switch" label="" disabled={user.role != "admin"} />
      <br />
      <CommissionInputGroup>
        <CommissionInput
          type="number"
          name="commissionRate"
          value={values.commissionRate}
          onChange={handleChange}
          onBlur={handleBlur}
          min="0"
          max="100"
          step="0.1"
          hasError={touched.commissionRate && errors.commissionRate}
          disabled={!isActive || user.role != "admin"}
        />
        <CommissionLabel>% Commission per order</CommissionLabel>
      </CommissionInputGroup>
      <br />
      <Button
        title="Save Commission Rate"
        styleButton="font-size=12px;padding:10px"
        onClick={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        disabled={user.role !== "admin"}
      />

      {touched.commissionRate && errors.commissionRate && (
        <ErrorMessage style={{ marginTop: "8px" }}>
          <AlertCircle size={12} />
          {errors.commissionRate}
        </ErrorMessage>
      )}
    </CommissionPlan>
  );
};

export default CommissionSettings;
